import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StatsRow } from '../types/client';
import { fmtEur, fmtNum } from '../utils/format';

const METRICS = [
  { key: 'importe_total_eur' as const, label: 'Importe', fmt: fmtEur, compact: compactEur },
  { key: 'total_pedidos'     as const, label: 'Pedidos',  fmt: fmtNum, compact: compactNum },
  { key: 'total_lineas'      as const, label: 'Líneas',   fmt: fmtNum, compact: compactNum },
  { key: 'total_clientes'    as const, label: 'Clientes', fmt: fmtNum, compact: compactNum },
];
type MetricKey = typeof METRICS[number]['key'];

const MONTH_LABELS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function compactNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.', ',')}k`;
  return String(Math.round(n));
}
function compactEur(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')}M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k€`;
  return `${Math.round(n)}€`;
}

function fmtFecmes(f: string): string {
  const [y, m] = f.split('-');
  return `${MONTH_LABELS[Number(m) - 1]} ${y.slice(2)}`;
}

function fmtKey(f: string, yearMode: boolean): string {
  if (yearMode) return MONTH_LABELS[Number(f) - 1] ?? f;
  return fmtFecmes(f);
}

function niceMax(val: number): number {
  if (val <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(val)));
  const n = val / mag;
  if (n <= 1) return mag;
  if (n <= 2) return 2 * mag;
  if (n <= 5) return 5 * mag;
  return 10 * mag;
}

const MT = 16, MB = 40, ML = 80, MR = 20, CHART_H = 240;
const SVG_H = MT + CHART_H + MB;
const COLOR_MAIN    = 'var(--accent)';
const COLOR_COMPARE = '#6B9BD2';

interface Props {
  main: StatsRow[];
  compare: StatsRow[] | null;
  mainLabel?: string;
  compareLabel?: string;
  metrics?: MetricKey[];
  yearMode?: boolean;
}

const StatsChart: React.FC<Props> = ({
  main,
  compare,
  mainLabel = 'Principal',
  compareLabel = 'Comparativo',
  metrics: metricFilter,
  yearMode = false,
}) => {
  const visibleMetrics = metricFilter
    ? METRICS.filter(m => metricFilter.includes(m.key))
    : METRICS;

  const [metric, setMetric] = useState<MetricKey>(
    () => metricFilter?.[0] ?? 'importe_total_eur'
  );
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [tooltipX, setTooltipX] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [svgW, setSvgW] = useState(800);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0) setSvgW(w);
    });
    ro.observe(el);
    setSvgW(el.clientWidth || 800);
    return () => ro.disconnect();
  }, []);

  const chartW = svgW - ML - MR;

  const allFecmes = useMemo(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    if (yearMode) {
      const s = new Set<string>();
      main.forEach(r => { if (r.fecmes < currentMonth) s.add(r.fecmes.slice(5, 7)); });
      compare?.forEach(r => { if (r.fecmes < currentMonth) s.add(r.fecmes.slice(5, 7)); });
      return Array.from(s).sort();
    }

    const s = new Set<string>();
    main.forEach(r => { if (r.fecmes < currentMonth) s.add(r.fecmes); });
    compare?.forEach(r => { if (r.fecmes < currentMonth) s.add(r.fecmes); });
    return Array.from(s).sort();
  }, [main, compare, yearMode]);

  const metaDef = METRICS.find(m => m.key === metric)!;

  const mainMap = useMemo(() => {
    const m: Record<string, number> = {};
    main.forEach(r => {
      const key = yearMode ? r.fecmes.slice(5, 7) : r.fecmes;
      m[key] = (r as any)[metric] ?? 0;
    });
    return m;
  }, [main, metric, yearMode]);

  const compareMap = useMemo(() => {
    if (!compare) return null;
    const m: Record<string, number> = {};
    compare.forEach(r => {
      const key = yearMode ? r.fecmes.slice(5, 7) : r.fecmes;
      m[key] = (r as any)[metric] ?? 0;
    });
    return m;
  }, [compare, metric, yearMode]);

  const maxVal = useMemo(() => {
    let max = 0;
    allFecmes.forEach(f => {
      max = Math.max(max, mainMap[f] ?? 0);
      if (compareMap) max = Math.max(max, compareMap[f] ?? 0);
    });
    return niceMax(max);
  }, [allFecmes, mainMap, compareMap]);

  const n = allFecmes.length;

  function xFor(i: number) {
    return ML + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  }
  function yFor(v: number) {
    return MT + CHART_H - (v / maxVal) * CHART_H;
  }

  const yTicks = [0, 1, 2, 3, 4].map(i => (maxVal / 4) * i);

  const xStep = Math.max(1, Math.ceil(n / Math.floor(chartW / 56)));

  const mainPolyline  = allFecmes.map((f, i) => `${xFor(i)},${yFor(mainMap[f] ?? 0)}`).join(' ');
  const cmpPolyline   = compareMap
    ? allFecmes.map((f, i) => `${xFor(i)},${yFor(compareMap[f] ?? 0)}`).join(' ')
    : null;

  function handleMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    if (n === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left - ML;
    const raw = (mx / chartW) * (n - 1);
    const idx = Math.max(0, Math.min(n - 1, Math.round(raw)));
    setHoverIdx(idx);
    setTooltipX(e.clientX - rect.left);
  }

  if (n === 0) return null;

  const tipIdx = hoverIdx ?? null;

  return (
    <div className="stats-chart-wrap" ref={wrapRef}>

      <div className="stats-chart-metric-row">
        {visibleMetrics.map(m => (
          <button
            key={m.key}
            className={`stats-chart-metric-btn${metric === m.key ? ' active' : ''}`}
            onClick={() => setMetric(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ position: 'relative' }}>
        <svg
          width={svgW}
          height={SVG_H}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Y grid + labels */}
          {yTicks.map(t => (
            <g key={t}>
              <line
                x1={ML} y1={yFor(t)} x2={ML + chartW} y2={yFor(t)}
                stroke="var(--border)" strokeWidth={t === 0 ? 1 : 0.5}
              />
              <text
                x={ML - 8} y={yFor(t)}
                textAnchor="end" dominantBaseline="middle"
                fontSize={11} fill="var(--text-secondary)"
              >
                {metaDef.compact(t)}
              </text>
            </g>
          ))}

          {/* X labels */}
          {allFecmes.map((f, i) => {
            if (i % xStep !== 0 && i !== n - 1) return null;
            return (
              <text
                key={f}
                x={xFor(i)} y={MT + CHART_H + 20}
                textAnchor="middle" fontSize={11} fill="var(--text-secondary)"
              >
                {fmtKey(f, yearMode)}
              </text>
            );
          })}

          {/* Compare line */}
          {cmpPolyline && (
            <polyline
              points={cmpPolyline}
              fill="none" stroke={COLOR_COMPARE}
              strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
              opacity={0.85}
            />
          )}

          {/* Main line */}
          <polyline
            points={mainPolyline}
            fill="none" stroke={COLOR_MAIN}
            strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round"
          />

          {/* Hover crosshair + dots */}
          {tipIdx !== null && (
            <>
              <line
                x1={xFor(tipIdx)} y1={MT}
                x2={xFor(tipIdx)} y2={MT + CHART_H}
                stroke="var(--text-secondary)" strokeWidth={1} strokeDasharray="3 3"
              />
              <circle cx={xFor(tipIdx)} cy={yFor(mainMap[allFecmes[tipIdx]] ?? 0)} r={4} fill={COLOR_MAIN} />
              {compareMap && (
                <circle cx={xFor(tipIdx)} cy={yFor(compareMap[allFecmes[tipIdx]] ?? 0)} r={4} fill={COLOR_COMPARE} />
              )}
            </>
          )}
        </svg>

        {/* Tooltip */}
        {tipIdx !== null && (
          <div
            className="stats-chart-tooltip"
            style={{
              left: Math.min(tooltipX + 14, svgW - 170),
              top: 10,
            }}
          >
            <div className="stats-chart-tooltip-date">{fmtKey(allFecmes[tipIdx], yearMode)}</div>
            <div className="stats-chart-tooltip-row">
              <span className="stats-chart-tooltip-dot" style={{ background: COLOR_MAIN }} />
              <span>{mainLabel}</span>
              <span className="stats-chart-tooltip-val">{metaDef.fmt(mainMap[allFecmes[tipIdx]] ?? 0)}</span>
            </div>
            {compareMap && (
              <div className="stats-chart-tooltip-row">
                <span className="stats-chart-tooltip-dot" style={{ background: COLOR_COMPARE }} />
                <span>{compareLabel}</span>
                <span className="stats-chart-tooltip-val">{metaDef.fmt(compareMap[allFecmes[tipIdx]] ?? 0)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {compareMap && (
        <div className="stats-chart-legend">
          <span className="stats-chart-legend-item">
            <span className="stats-chart-legend-dot" style={{ background: COLOR_MAIN }} />
            {mainLabel}
          </span>
          <span className="stats-chart-legend-item">
            <span className="stats-chart-legend-dot" style={{ background: COLOR_COMPARE }} />
            {compareLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default StatsChart;
